/**
 * @file course-share.schema.ts
 * @description Schema Zod para a coleção 'courseShares' — compartilhamentos
 * de vídeos entre parceiros. Apesar do nome legado "courseShares", o mecanismo
 * é genérico: aceita qualquer subtype de Video (COURSE, PODCAST, DOCUMENTARY,
 * SERIES, LIVE). O parceiro origem escolhe vídeos para compartilhar; o
 * destino importa via shareCode.
 *
 * NOMENCLATURA: o campo `videoIds` (antigo `courseIds`) é o canônico. Aceita
 * docs legados via preprocess para retrocompat sem migração.
 */

import { z } from 'zod';

// --- Schema Principal ---

const CourseShareBaseSchema = z.object({
  id: z.string(),

  // Parceiro que está compartilhando (origem)
  sourcePartnerId: z.string(),

  // Parceiro que recebe o compartilhamento (destino)
  targetPartnerId: z.string(),

  // Código único de compartilhamento (8 chars alfanuméricos)
  // Único por targetPartnerId — mesmo código para todos os vídeos com aquele parceiro
  shareCode: z.string().length(8),

  // IDs dos vídeos compartilhados (qualquer subtype). Renomeado de `courseIds`.
  videoIds: z.array(z.string()).default([]),

  // Status do compartilhamento
  status: z.enum(['ACTIVE', 'REVOKED']).default('ACTIVE'),

  // Campos de auditoria
  createdAt: z.date(),
  updatedAt: z.date(),
});

/**
 * Preprocessor aceita docs legados com `courseIds` em vez de `videoIds`.
 * Janela de migração silenciosa — eventualmente removível.
 */
const CourseShareSchema = z.preprocess(
  (raw) => {
    if (typeof raw !== 'object' || raw === null) return raw;
    const v = raw as Record<string, unknown>;
    if (v.videoIds === undefined && Array.isArray(v.courseIds)) {
      return { ...v, videoIds: v.courseIds };
    }
    return v;
  },
  CourseShareBaseSchema,
);

// --- Inferred Types ---

type CourseShare = z.infer<typeof CourseShareSchema>;

// --- Public Exports ---

export { CourseShareSchema };
export type { CourseShare };
